import HeroBackground from "./HeroBackground";

export default function Hero() {
  return (
    <section className="relative w-full h-screen min-h-[640px] overflow-hidden bg-[#070a1a]">
      {/* Deep space gradient base */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 35%, #1a1450 0%, #0b0d2e 45%, #050717 100%)",
        }}
      />

      {/* Soft color glow blobs (atmospheric bloom feel) */}
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(circle, rgba(99,102,241,0.55) 0%, rgba(99,102,241,0) 60%)",
          filter: "blur(40px)",
        }}
      />
      <div
        className="absolute bottom-[-200px] left-[-200px] w-[700px] h-[700px] rounded-full pointer-events-none opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(56,189,248,0.45) 0%, rgba(56,189,248,0) 65%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="absolute top-[10%] right-[-180px] w-[600px] h-[600px] rounded-full pointer-events-none opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(168,85,247,0.5) 0%, rgba(168,85,247,0) 65%)",
          filter: "blur(60px)",
        }}
      />

      {/* WebGL animated background */}
      <HeroBackground />

      {/* Color-tinted overlay for depth + text contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f2c]/40 via-transparent to-[#05071a]/80 pointer-events-none" />

      {/* Top navigation */}
      <header className="absolute top-0 left-0 right-0 z-20">
        <nav className="flex items-center justify-between px-6 md:px-12 py-6">
          <a
            href="/"
            className="group flex items-center gap-2.5 cursor-pointer select-none"
            aria-label="Home"
          >
            {/* Geometric mark */}
            <span className="relative w-8 h-8 flex items-center justify-center">
              {/* Outer rotating ring */}
              <span
                className="absolute inset-0 rounded-[10px] border border-white/30 group-hover:border-violet-300/70 transition-colors duration-500"
                style={{ transform: "rotate(45deg)" }}
              />
              {/* Inner solid square */}
              <span
                className="absolute w-3.5 h-3.5 bg-white group-hover:bg-violet-300 transition-colors duration-500"
                style={{
                  transform: "rotate(45deg)",
                  boxShadow: "0 0 12px rgba(167, 139, 250, 0.6)",
                }}
              />
              {/* Center dot */}
              <span className="absolute w-1 h-1 rounded-full bg-[#070a1a]" />
            </span>

            {/* Wordmark */}
            <span className="flex items-baseline">
              <span
                className="text-white text-[15px] uppercase"
                style={{
                  fontFamily: '"Syncopate", sans-serif',
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                }}
              >
                Nova
              </span>
              <span
                className="text-white/50 text-[15px] uppercase ml-[2px]"
                style={{
                  fontFamily: '"Syncopate", sans-serif',
                  fontWeight: 400,
                  letterSpacing: "0.18em",
                }}
              >
                Lab
              </span>
              {/* Trailing index marker */}
              <span
                className="ml-2 text-[9px] text-violet-300/80 uppercase tracking-[0.3em] translate-y-[-6px]"
                style={{ fontFamily: '"Chakra Petch", sans-serif' }}
              >
                /01
              </span>
            </span>
          </a>

          <ul className="hidden md:flex items-center gap-8 text-sm text-white/90">
            <li>
              <a
                href="#features"
                className="hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Features
              </a>
            </li>
            <li>
              <a
                href="#showcase"
                className="hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Showcase
              </a>
            </li>
            <li>
              <a
                href="#pricing"
                className="hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Pricing
              </a>
            </li>
            <li>
              <a
                href="#about"
                className="hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                About
              </a>
            </li>
          </ul>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="#signin"
              className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              Sign in
            </a>
            <a
              href="#get-started"
              className="text-sm text-white bg-violet-500 hover:bg-violet-400 transition-colors px-5 py-2 rounded-full cursor-pointer whitespace-nowrap"
            >
              Get started
            </a>
          </div>

          <button
            type="button"
            aria-label="Open menu"
            className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className="ri-menu-line text-2xl" />
          </button>
        </nav>
      </header>

      {/* Hero content */}
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-6">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-md border border-indigo-300/20 text-xs md:text-sm text-white/90 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
        </span>

        {/* Hero headline - sophisticated sci-fi display font */}
        <h1
          className="text-white text-3xl md:text-5xl lg:text-[64px] leading-[1.1] tracking-[0.02em] uppercase text-center mx-auto"
          style={{
            fontFamily: '"Syncopate", "Chakra Petch", sans-serif',
            fontWeight: 400,
            textShadow: "0 0 30px rgba(139, 92, 246, 0.35)",
          }}
        >
          <span className="block">Engineered for</span>
          <span className="block mt-2 md:mt-3 text-white" style={{ fontWeight: 700 }}>
            The Next Era
            <span className="hero-caret" aria-hidden="true" />
          </span>
        </h1>

        {/* Thin horizontal divider line */}
        <div className="mt-10 flex items-center gap-4">
          <span className="block w-16 md:w-24 h-px bg-gradient-to-r from-transparent to-indigo-300/60" />
          <span
            className="text-[10px] md:text-xs tracking-[0.5em] text-indigo-200/80 uppercase whitespace-nowrap"
            style={{ fontFamily: '"Chakra Petch", sans-serif', fontWeight: 300 }}
          >
            Build · Render · Deploy
          </span>
          <span className="block w-16 md:w-24 h-px bg-gradient-to-l from-transparent to-indigo-300/60" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center text-white/60 text-xs">
        <span className="mb-2 whitespace-nowrap">Scroll to explore</span>
        <div className="w-6 h-10 rounded-full border border-white/30 flex items-start justify-center p-1.5">
          <span className="block w-1 h-2 rounded-full bg-white/70 animate-bounce" />
        </div>
      </div>
    </section>
  );
}